import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { api, ApiError, unwrap } from "@/api/client";
import { useAddQueryToDashboard, useDashboards, type QueryResult } from "@/api/queries";
import { ArtifactCard } from "@/components/artifacts/ArtifactCard";
import { Button } from "@/components/ui/Button";
import { Chip } from "@/components/ui/Chip";
import { EmptyState } from "@/components/ui/EmptyState";
import { Select } from "@/components/ui/Select";
import { AlertIcon, CodeIcon, ShieldIcon } from "@/components/icons";
import type { Artifact } from "@/artifacts/types";
import { cn } from "@/lib/cn";
import { usePreferredSource } from "@/lib/preferredSource";

/**
 * The read-only SQL workbench (PRD FR-5.5).
 *
 * The same guarded path everything else uses — `POST /api/connections/{id}/query`
 * runs SQLGuard, the engine's read-only session, the row cap and the statement
 * timeout — so this is an escape hatch for the analyst, not a hole in the
 * safety model. A rejected statement comes back with the guard's own reason
 * code, exactly as it does in chat and in Edit SQL.
 *
 * Results render as a table artifact through M3's renderer rather than a second
 * table implementation, which is also what makes "pin this to a dashboard"
 * one line rather than a feature.
 */

const PLACEHOLDER = `SELECT p.name, SUM(oi.quantity * oi.unit_price) AS net_sales
FROM order_items oi
JOIN products p ON p.id = oi.product_id
GROUP BY 1
ORDER BY 2 DESC
LIMIT 10`;

/**
 * The result as the artifact renderer already understands it.
 *
 * Built here rather than fetched because a workbench result belongs to nobody
 * yet — there is no message and no tile to materialise it from. The moment it
 * is pinned, the server materialises it exactly as it does every other tile,
 * so this shape is a preview and never a second source of truth.
 */
function toArtifact(result: QueryResult, title: string): Artifact {
  return {
    type: "table",
    title,
    index: 0,
    family: null,
    chart: null,
    items: [],
    notes: [],
    text: "",
    x: "",
    y: "",
    series_by: "",
    size: "",
    swap_options: [],
    degraded_from: null,
    from_query: null,
    data: {
      columns: result.columns.map((name) => ({
        key: name,
        label: name,
        format: "text",
        numeric: result.rows.some((row) => typeof row[result.columns.indexOf(name)] === "number"),
      })),
      rows: result.rows,
      row_count: result.row_count,
      total_rows: null,
      truncated: result.truncated,
    },
  };
}

/**
 * A CSV of the raw values, made in the browser.
 *
 * A workbench result is not stored anywhere, so there is no server export to
 * link to — but the values are right here, and the rule that CSV carries the
 * untouched numbers (PRD FR-6.3) is as easy to keep here as anywhere.
 */
function useCsvUrl(result: QueryResult | undefined): string {
  const [url, setUrl] = useState("");
  useEffect(() => {
    if (!result) {
      setUrl("");
      return;
    }
    const escape = (value: unknown) => {
      const text = value == null ? "" : String(value);
      return /[",\n]/.test(text) ? `"${text.replaceAll('"', '""')}"` : text;
    };
    const csv = [
      result.columns.map(escape).join(","),
      ...result.rows.map((row) => row.map(escape).join(",")),
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const next = URL.createObjectURL(blob);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [result]);
  return url;
}

/** "Pin this to a dashboard" (PRD FR-5.5). The server re-runs and materialises. */
function PinToDashboard({ connectionId, sql }: { connectionId: string; sql: string }) {
  const { data: dashboards } = useDashboards();
  const pin = useAddQueryToDashboard();
  const [target, setTarget] = useState("");
  const [title, setTitle] = useState("");

  if (!dashboards || dashboards.length === 0) {
    return (
      <span className="type-support text-ink-disabled">
        Create a dashboard to pin results to it.
      </span>
    );
  }
  return (
    <div className="flex flex-wrap items-center gap-2">
      <input
        value={title}
        onChange={(event) => setTitle(event.target.value)}
        placeholder="Tile title"
        className="border-border bg-card text-ink rounded-element w-40 border px-2 py-1 type-support"
      />
      <select
        value={target}
        onChange={(event) => setTarget(event.target.value)}
        className="border-border bg-card text-ink rounded-element border px-2 py-1 type-support"
      >
        <option value="">Pick a dashboard…</option>
        {dashboards.map((dashboard) => (
          <option key={dashboard.id} value={dashboard.id}>
            {dashboard.name}
          </option>
        ))}
      </select>
      <Button
        type="button"
        loading={pin.isPending}
        disabled={!target || !sql}
        onClick={() => pin.mutate({ dashboardId: target, connection_id: connectionId, sql, title })}
      >
        Pin
      </Button>
      {pin.isSuccess ? <span className="type-support text-success">Pinned</span> : null}
    </div>
  );
}

export function WorkbenchPage() {
  // Where you came from wins; otherwise the source you last worked with. The
  // alphabetically-first source is the last resort, not the default — that is
  // how a first query used to land on whichever source happened to sort first
  // (backlog item 7).
  const { connection: from } = useSearch({ from: "/app/workbench" });
  const { sources: connections, connectionId: active, select } = usePreferredSource(from);
  const navigate = useNavigate();
  const [sql, setSql] = useState("");
  const [ran, setRan] = useState("");

  const connection = connections.find((row) => row.id === active);

  // Arriving with an explicit source makes it the one you were last working
  // with, so the next visit — and the next conversation — starts here too.
  // Guarded on `from === active`, so a link to a source that has since been
  // deleted remembers nothing rather than remembering an id nothing answers to.
  useEffect(() => {
    if (from && from === active) select(active);
  }, [from, active, select]);

  /**
   * Picking a source rewrites the URL as well as the memory.
   *
   * Without this, `?connection=` would keep winning over the choice just made
   * — the picker would snap back on the next render and reading the page would
   * be the only way to find out why. Replacing rather than pushing, because
   * changing the source is not a place you want the back button to return to.
   */
  const choose = (id: string) => {
    select(id);
    void navigate({ to: "/workbench", search: { connection: id }, replace: true });
  };

  const run = useMutation({
    // This page *is* the error surface: a guard refusal is the answer it
    // exists to show, so it stands down from the global toast (item 11).
    meta: { errorSurface: "inline" } as const,
    mutationFn: async (statement: string) =>
      unwrap(
        await api.POST("/api/connections/{connection_id}/query", {
          params: { path: { connection_id: active } },
          body: { sql: statement },
        }),
      ),
    onSuccess: (_result, statement) => setRan(statement),
  });

  const artifact = useMemo(
    () => (run.data ? toArtifact(run.data, "Query result") : null),
    [run.data],
  );
  const csvUrl = useCsvUrl(run.data);
  const error = run.error instanceof ApiError ? run.error : null;
  const blocked = error?.code === "sql_rejected";

  return (
    <div className="mx-auto w-full max-w-5xl px-4 py-8 sm:px-8 sm:py-10">
      <header className="mb-6">
        <p className="type-caption text-ink-disabled">Workspace</p>
        <h1 className="type-h1 text-ink mt-2">SQL workbench</h1>
        <p className="type-body text-ink-secondary mt-3 max-w-2xl">
          Ad-hoc SQL against a source, through the same guard every other path uses. Useful when
          you already know the query — and as the nudge when the model needs one.
        </p>
      </header>

      {connections.length === 0 ? (
        <EmptyState
          icon={<CodeIcon className="size-6" />}
          title="No sources yet"
          detail="Add a database or upload a CSV first."
        />
      ) : (
        <div className="flex flex-col gap-4">
          <div className="w-72">
            <Select
              label="Source"
              value={active}
              onChange={(event) => choose(event.target.value)}
            >
              {connections.map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name}
                </option>
              ))}
            </Select>
          </div>

          <label className="flex flex-col gap-2">
            <span className="type-label text-ink">Statement</span>
            <textarea
              value={sql}
              onChange={(event) => setSql(event.target.value)}
              onKeyDown={(event) => {
                // The shortcut every SQL editor has, and the reason this page
                // is usable rather than merely present.
                if ((event.metaKey || event.ctrlKey) && event.key === "Enter" && sql.trim()) {
                  event.preventDefault();
                  run.mutate(sql);
                }
              }}
              rows={10}
              spellCheck={false}
              placeholder={PLACEHOLDER}
              className="border-border bg-card text-ink rounded-element w-full border p-3 type-code"
            />
          </label>

          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="primary"
              type="button"
              loading={run.isPending}
              disabled={!sql.trim()}
              onClick={() => run.mutate(sql)}
            >
              Run
            </Button>
            <span className="type-support text-ink-disabled">⌘/Ctrl + Enter</span>
            {connection ? (
              <span className="type-support text-ink-secondary inline-flex items-center gap-1.5">
                <ShieldIcon className="size-4" />
                Read-only · {connection.row_cap} row cap ·{" "}
                {connection.statement_timeout_seconds}s timeout
              </span>
            ) : null}
          </div>

          {error ? (
            <div
              className={cn(
                "rounded-container border p-4",
                blocked ? "border-error/40 bg-error-subtle/30" : "border-warning/40 bg-warning-subtle/30",
              )}
            >
              <p className="type-label text-ink inline-flex items-center gap-2">
                <AlertIcon className="size-4" />
                {blocked ? "SQLGuard refused this statement" : "The query failed"}
              </p>
              <p className="type-support text-ink-secondary mt-1">{error.message}</p>
              <Chip tone={blocked ? "error" : "warning"} className="mt-2">
                {error.code}
              </Chip>
              {blocked ? (
                <p className="type-support text-ink-secondary mt-2">
                  Nothing reached the database. The attempt is in the audit trail.
                </p>
              ) : null}
            </div>
          ) : null}

          {run.data && artifact ? (
            <div className="flex flex-col gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <Chip tone="success">
                  {run.data.row_count} row{run.data.row_count === 1 ? "" : "s"}
                </Chip>
                <Chip tone="neutral">{run.data.duration_ms} ms</Chip>
                {run.data.truncated ? (
                  <Chip tone="warning">truncated at {run.data.row_cap}</Chip>
                ) : null}
                {run.data.note ? (
                  <span className="type-support text-ink-secondary">{run.data.note}</span>
                ) : null}
                <div className="ms-auto">
                  <PinToDashboard connectionId={active} sql={ran} />
                </div>
              </div>
              <ArtifactCard artifact={artifact} csvUrl={csvUrl} />
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
